import { useMemo, useState, type CSSProperties } from "react";
import { FieldEditor } from "./FieldEditor";
import type { FieldDefinition, ResolvedRef } from "../../../lib/types";

/**
 * Sprint 12 — the record's structured fields as a vertical labeled list
 * (Notion / Linear / Airtable convergence): one field per row, label left,
 * type-appropriate inline editor right. Fields are grouped by their `section`
 * hint (ungrouped first), ordered by `importance` within each group, and
 * `detail`-importance fields collapse behind "Show all fields" when the record
 * has many. Fields whose `display_role` is title/subtitle/badge render in the
 * header instead and are excluded here to avoid duplication.
 */

interface FieldListProps {
  fields: FieldDefinition[];
  values: Record<string, unknown>;
  forwardRefs: Record<string, ResolvedRef[]>;
  onSave: (fieldName: string, value: unknown) => void;
  onOpenRef?: (entityTypeSlug: string, entityId: string) => void;
}

const HEADER_ROLES = new Set(["title", "subtitle", "badge"]);
const SHOW_MORE_THRESHOLD = 10;

const IMPORTANCE_RANK: Record<string, number> = {
  primary: 0,
  secondary: 1,
  supporting: 2,
  detail: 3,
};

function fieldLabel(field: FieldDefinition): string {
  return field.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function FieldList({ fields, values, forwardRefs, onSave, onOpenRef }: FieldListProps) {
  const [showAll, setShowAll] = useState(false);

  const visible = useMemo(
    () =>
      fields.filter(
        (f) => !HEADER_ROLES.has(f.display_role ?? "") && (f.display_role as string) !== "hidden",
      ),
    [fields],
  );

  // Group by section (ungrouped first), preserving first-occurrence order, then
  // importance-sort within each group.
  const groups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, FieldDefinition[]>();
    for (const f of visible) {
      const key = f.section ?? "";
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key)!.push(f);
    }
    // Ungrouped ("") always first.
    order.sort((a, b) => (a === "" ? -1 : b === "" ? 1 : 0));
    return order.map((section) => ({
      section,
      fields: [...map.get(section)!].sort(
        (a, b) => (IMPORTANCE_RANK[a.importance ?? "secondary"] ?? 1) - (IMPORTANCE_RANK[b.importance ?? "secondary"] ?? 1),
      ),
    }));
  }, [visible]);

  const detailCount = visible.filter((f) => f.importance === "detail").length;
  const collapseDetail = visible.length > SHOW_MORE_THRESHOLD && detailCount > 0 && !showAll;

  if (visible.length === 0) {
    return (
      <div style={{ fontSize: 13, color: "var(--color-text-tertiary)", padding: "8px 0" }}>
        This type has no fields yet.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {groups.map((group) => {
        const rows = group.fields.filter((f) => !(collapseDetail && f.importance === "detail"));
        if (rows.length === 0) return null;
        return (
          <div key={group.section || "_ungrouped"} style={{ marginBottom: group.section ? 4 : 0 }}>
            {group.section && <div style={sectionHeaderStyle}>{titleCase(group.section)}</div>}
            {rows.map((f) => (
              <div key={f.name} style={rowStyle}>
                <div style={labelStyle} title={fieldLabel(f)}>
                  {fieldLabel(f)}
                  {f.required && <span style={{ color: "var(--c-red-600)", marginLeft: 2 }}>*</span>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <FieldEditor
                    field={f}
                    value={values[f.name]}
                    refs={forwardRefs[f.name]}
                    onSave={(v) => onSave(f.name, v)}
                    onOpenRef={onOpenRef}
                  />
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {collapseDetail && (
        <button onClick={() => setShowAll(true)} style={showMoreStyle}>
          Show all fields ({detailCount} more)
        </button>
      )}
    </div>
  );
}

function titleCase(s: string): string {
  return s.replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "3px 0",
};

const labelStyle: CSSProperties = {
  width: 130,
  flexShrink: 0,
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  paddingTop: 6,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const sectionHeaderStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  padding: "10px 0 4px",
};

const showMoreStyle: CSSProperties = {
  alignSelf: "flex-start",
  marginTop: 6,
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-info)",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  padding: "2px 0",
};
